import axios from "axios";

export type SupportCalendarId = string & { __brand: "SupportCalendarId" };

export type SupportCalendarDay = {
    day: number;
    openMinute: number;
    closeMinute: number;
};

export type SupportCalendarHoliday = {
    date: string;
    name: string;
    /** The bundled set this came from; null for holidays added by hand. */
    presetKey: string | null;
    presetYear: number | null;
};

export type HolidayPreset = {
    key: string;
    name: string;
};

export type PresetHoliday = {
    date: string;
    name: string;
};

export type SupportCalendar = {
    id: SupportCalendarId;
    name: string;
    timeZone: string;
    projectCount: number;
    days: SupportCalendarDay[];
    holidays: SupportCalendarHoliday[];
};

export type SupportCalendarIndex = {
    calendars: SupportCalendar[];
    timeZones: string[];
};

export type SupportCalendarInput = {
    name: string;
    timeZone: string;
    days: SupportCalendarDay[];
    holidays: SupportCalendarHoliday[];
};

export interface SupportCalendarsApi {
    get(): Promise<SupportCalendarIndex>;
    create(name: string, timeZone: string): Promise<SupportCalendar>;
    update(id: SupportCalendarId, input: SupportCalendarInput): Promise<SupportCalendar>;
    remove(id: SupportCalendarId): Promise<void>;
    holidayPresets(): Promise<HolidayPreset[]>;
    presetHolidays(preset: string, year: number): Promise<PresetHoliday[]>;
}

export const supportCalendarsApi: SupportCalendarsApi = {
    async get() {
        const { data } = await axios.get<SupportCalendarIndex>("/api/v0/support-calendars");
        return data;
    },
    async create(name, timeZone) {
        const { data } = await axios.post<SupportCalendar>("/api/v0/support-calendars", {
            name,
            timeZone,
        });
        return data;
    },
    async update(id, input) {
        const { data } = await axios.put<SupportCalendar>(`/api/v0/support-calendars/${id}`, input);
        return data;
    },
    async remove(id) {
        await axios.delete(`/api/v0/support-calendars/${id}`);
    },
    async holidayPresets() {
        const { data } = await axios.get<HolidayPreset[]>(
            "/api/v0/support-calendars/holiday-presets"
        );
        return data;
    },
    async presetHolidays(preset, year) {
        const { data } = await axios.get<PresetHoliday[]>(
            `/api/v0/support-calendars/holiday-presets/${preset}/${year}`
        );
        return data;
    },
};
